import { create } from "zustand";
import { persist } from "zustand/middleware";

export enum SideMenuBehavior {
  AUTO = "auto",
  PINNED = "pinned",
}

interface DropdownState {
  isOpen: boolean;
  projectId: string | null;
  position: { x: number; y: number };
}

interface SideMenuActions {
  open: () => void;
  close: () => void;
  openPersonalSettings: (initialSection?: string) => void;
  closePersonalSettings: () => void;
  allowClose: () => void;
  setSelectedProjectId: (projectId: string | null) => void;
  switchBehavior: () => void;

  // Dropdown actions
  openDropdown: (projectId: string, position: { x: number; y: number }) => void;
  closeDropdown: () => void;

  setCanReopenMenu: (canReopen: boolean) => void;
}

interface SideMenuState {
  behavior: SideMenuBehavior;
  isOpen: boolean;
  arePersonalSettingsOpen: boolean;
  initialPersonalSettingsSection: string | null;
  canClose: boolean;
  selectedProjectId: string | null;
  timeoutRef: NodeJS.Timeout | null;
  dropdownState: DropdownState;
  canReopenMenu: boolean;

  actions: SideMenuActions;
}

const useSideMenuStore = create<SideMenuState>()(
  persist(
    (set, get) => {
      return {
        behavior: SideMenuBehavior.AUTO,
        isOpen: false,
        arePersonalSettingsOpen: false,
        initialPersonalSettingsSection: null,
        canClose: true,
        selectedProjectId: null,
        timeoutRef: null,
        dropdownState: {
          isOpen: false,
          projectId: null,
          position: { x: 0, y: 0 },
        },
        canReopenMenu: true,

        actions: {
          open: () => {
            set({ isOpen: true });
          },

          close: () => {
            if (get().canClose) {
              set({
                isOpen: false,
                arePersonalSettingsOpen: false,
                dropdownState: {
                  isOpen: false,
                  projectId: null,
                  position: { x: 0, y: 0 },
                },
              });
            }
          },

          openPersonalSettings: (initialSection?: string) => {
            set({
              arePersonalSettingsOpen: true,
              initialPersonalSettingsSection: initialSection || null,
            });
          },

          closePersonalSettings: () => {
            set({
              canClose: false,
              arePersonalSettingsOpen: false,
              initialPersonalSettingsSection: null,
            });
          },

          allowClose: () => {
            set({ canClose: true });
          },

          setSelectedProjectId: (projectId: string | null) => {
            set({ selectedProjectId: projectId });
          },

          switchBehavior: () => {
            set((state) => ({
              behavior:
                state.behavior === SideMenuBehavior.PINNED
                  ? SideMenuBehavior.AUTO
                  : SideMenuBehavior.PINNED,
            }));
          },

          openDropdown: (
            projectId: string,
            position: { x: number; y: number }
          ) => {
            set({
              dropdownState: {
                isOpen: true,
                projectId,
                position,
              },
            });
          },

          closeDropdown: () => {
            set(() => ({
              dropdownState: {
                isOpen: false,
                projectId: null,
                position: { x: 0, y: 0 },
              },
            }));
          },

          setCanReopenMenu: (canReopen: boolean) =>
            set({ canReopenMenu: canReopen }),
        },
      };
    },
    {
      name: "side-menu",
      partialize: (state) => ({ behavior: state.behavior }),
    }
  )
);

export const useIsSideMenuOpen = () =>
  useSideMenuStore((state) => state.isOpen);

export const useSideMenuBehavior = () =>
  useSideMenuStore((state) => state.behavior);

export const useArePersonalSettingsOpen = () =>
  useSideMenuStore((state) => state.arePersonalSettingsOpen);

export const useInitialPersonalSettingsSection = () =>
  useSideMenuStore((state) => state.initialPersonalSettingsSection);

export const useCanReopenSideMenu = () =>
  useSideMenuStore((state) => state.canReopenMenu);

export const useSideMenuSelectedProjectId = () =>
  useSideMenuStore((state) => state.selectedProjectId);

export const useSideMenuDropdownState = () =>
  useSideMenuStore((state) => state.dropdownState);

export const useSideMenuActions = () =>
  useSideMenuStore((state) => state.actions);
